import { useState } from 'react';
import type { ChannelConnection } from '@arcnet/contracts';
import { useSession } from '../../lib/auth/session.js';
import { useQueues } from '../../lib/api/hooks-tms.js';
import { useChannelConnections, useChannelMutations } from '../../lib/api/hooks-conversations.js';
import { ApiClientError } from '../../lib/api/client.js';
import { Badge, CardHead, Empty, ErrorBanner, Segmented, SectionHead, TableSkeleton } from '../../components/ui/Primitives.js';
import { Icon } from '../../components/ui/Icon.js';
import { RowAction } from '../../components/ui/RowAction.js';
import { confirmAction } from '../../components/ui/Confirm.js';
import { relativeTime } from '../../lib/format.js';

/**
 * The account's WhatsApp numbers and Telegram bots (docs/43): what customers message, and the queue
 * each one's conversations wait in for an agent.
 *
 * WhatsApp is connected through 360dialog for now, with the channel's API key; connecting directly
 * through Meta's own sign-up comes once ArcNet's Meta app is approved (docs/43 §3.2). Telegram takes
 * a bot's token from BotFather. Either is checked with the provider before it is saved, and never
 * shown again: the last four characters say which one is in.
 */

const PROVIDER_LABEL: Record<ChannelConnection['provider'], string> = {
  telegram: 'Telegram', '360dialog': 'WhatsApp (360dialog)', meta: 'WhatsApp',
};

export function ChannelsTab({ tenantId }: { tenantId: string }) {
  const can = useSession((s) => s.can);
  const mayEdit = can('channel:write');
  const list = useChannelConnections(tenantId);
  const queues = useQueues(tenantId).data?.data ?? [];
  const { connect, update, disconnect } = useChannelMutations(tenantId);
  const [adding, setAdding] = useState(false);
  const rows = list.data?.data ?? [];
  const error = list.error ?? update.error ?? disconnect.error;

  return (
    <>
      <SectionHead title="WhatsApp & Telegram"
        sub="Messages customers send to these come into the queue you choose, and are offered to its agents like calls." />
      <div className="arc-card">
        <CardHead title="Channels" action={mayEdit && !adding ? (
          <button type="button" className="arc-btn arc-btn-sm arc-btn-primary" onClick={() => setAdding(true)}>
            <Icon name="plus" size={13} /> Connect
          </button>
        ) : undefined} />
        {error && <div className="arc-card-body"><ErrorBanner error={error} /></div>}
        {adding && <ConnectForm queues={queues} pending={connect.isPending} error={connect.error}
          onCancel={() => { setAdding(false); connect.reset(); }}
          onSubmit={(body) => connect.mutate(body, { onSuccess: () => setAdding(false) })} />}
        {list.data === undefined ? <table className="arc-table"><TableSkeleton query={list} cols={5} rows={2} /></table>
          : rows.length === 0 ? (
            <Empty icon="message" title="No channels"
              hint="Connect a WhatsApp number or a Telegram bot, and choose the queue its messages go to." />
          ) : (
            <div className="arc-table-wrap">
              <table className="arc-table">
                <thead><tr><th>Channel</th><th>Customers write to</th><th>Queue</th><th>Last message</th><th /></tr></thead>
                <tbody>
                  {rows.map((r) => (
                    <tr key={r.id}>
                      <td>
                        <div style={{ fontWeight: 550 }}>{r.name}</div>
                        <div className="arc-subtle" style={{ fontSize: 12 }}>
                          {PROVIDER_LABEL[r.provider]}{r.secret_hint ? ` · key …${r.secret_hint}` : ''}
                        </div>
                        {r.status === 'error' && <div className="arc-error">{r.last_error ?? 'The provider refused the last request.'}</div>}
                      </td>
                      <td className="arc-mono">
                        {r.address ?? <span className="arc-subtle">{r.kind === 'whatsapp' ? 'shown after the first message' : '—'}</span>}
                      </td>
                      <td>
                        <select className="arc-select" style={{ maxWidth: 220 }} aria-label={`Queue for ${r.name}`}
                          disabled={!mayEdit || update.isPending} value={r.queue_id ?? ''}
                          onChange={(e) => update.mutate({ id: r.id, body: { queue_id: e.target.value || null } })}>
                          <option value="">No queue: messages wait unassigned</option>
                          {queues.map((q) => <option key={q.id} value={q.id}>{q.name}</option>)}
                        </select>
                      </td>
                      <td className="arc-subtle">{r.last_inbound_at ? relativeTime(r.last_inbound_at) : 'none yet'}</td>
                      <td className="arc-td-right">
                        {r.status === 'connected' ? <Badge tone="ok">connected</Badge> : <Badge tone="danger">problem</Badge>}
                        {mayEdit && (
                          <RowAction kind="delete" label={`Disconnect ${r.name}`} disabled={disconnect.isPending}
                            onClick={() => {
                              void confirmAction({
                                title: `Disconnect ${r.name}?`,
                                body: 'New messages to it stop reaching the account. Conversations already here are kept.',
                                action: 'Disconnect', danger: true,
                              }).then((ok) => { if (ok) disconnect.mutate(r.id); });
                            }} />
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
      </div>
    </>
  );
}

function ConnectForm({ queues, pending, error, onCancel, onSubmit }: {
  queues: Array<{ id: string; name: string }>;
  pending: boolean;
  error: unknown;
  onCancel: () => void;
  onSubmit: (body: { provider: 'telegram' | '360dialog'; name: string; secret: string; queue_id: string | null }) => void;
}) {
  const [kind, setKind] = useState<'telegram' | '360dialog'>('360dialog');
  const [name, setName] = useState('');
  const [secret, setSecret] = useState('');
  const [queueId, setQueueId] = useState('');
  const err = error instanceof ApiClientError ? error : null;
  const fieldErrors = err?.fieldErrors ?? {};
  const ready = name.trim() && secret.trim().length >= 10;

  return (
    <form className="arc-card-body" style={{ borderBottom: '1px solid var(--arc-border)' }}
      onSubmit={(e) => { e.preventDefault(); if (ready) onSubmit({ provider: kind, name: name.trim(), secret: secret.trim(), queue_id: queueId || null }); }}>
      <div className="arc-field">
        <span className="arc-label">Channel</span>
        <Segmented label="Channel" value={kind} onChange={setKind}
          options={[['360dialog', 'WhatsApp'], ['telegram', 'Telegram']] as const} />
      </div>
      <p className="arc-muted" style={{ margin: '0 0 12px', fontSize: 12.5 }}>
        {kind === 'telegram'
          ? 'Make a bot by messaging @BotFather on Telegram, then paste the token it gives you. Customers find the bot by its @name.'
          : 'Connect the number in your 360dialog hub, then paste the API key it gives for that number.'}
      </p>
      <label className="arc-field">
        <span className="arc-label">Name</span>
        <input className="arc-input" style={{ maxWidth: 320 }} value={name} maxLength={60} autoFocus
          placeholder={kind === 'telegram' ? 'Support bot' : 'Support WhatsApp'} onChange={(e) => setName(e.target.value)} />
        {fieldErrors['name'] && <div className="arc-error">{fieldErrors['name']}</div>}
      </label>
      <label className="arc-field">
        <span className="arc-label">{kind === 'telegram' ? 'Bot token' : 'API key'}</span>
        <input className="arc-input arc-mono" style={{ maxWidth: 420 }} type="password" autoComplete="off" value={secret}
          onChange={(e) => setSecret(e.target.value)} />
        {fieldErrors['secret'] && <div className="arc-error">{fieldErrors['secret']}</div>}
      </label>
      <label className="arc-field">
        <span className="arc-label">Queue</span>
        <select className="arc-select" style={{ maxWidth: 320 }} value={queueId} onChange={(e) => setQueueId(e.target.value)}>
          <option value="">No queue: messages wait unassigned</option>
          {queues.map((q) => <option key={q.id} value={q.id}>{q.name}</option>)}
        </select>
        {fieldErrors['queue_id'] && <div className="arc-error">{fieldErrors['queue_id']}</div>}
      </label>
      {err && !Object.keys(fieldErrors).length && <ErrorBanner error={err} />}
      <div className="arc-row" style={{ gap: 8 }}>
        <button type="submit" className="arc-btn arc-btn-sm arc-btn-primary" disabled={!ready || pending}>
          {pending ? 'Connecting…' : 'Connect'}
        </button>
        <button type="button" className="arc-btn arc-btn-sm" onClick={onCancel}>Cancel</button>
      </div>
    </form>
  );
}
